/* Qué hacer con lo que salió de la foto del registro.
 *
 * ── LO QUE LEE UN MODELO NO SE GUARDA SOLO ──────────────────────────────────
 *
 * Es la regla de `vale_foto.py` y el «Ok / Editar» que pidió Marcos ES esa
 * regla. Allá, un dígito mal leído inventa un hueco y tapa otro; acá **una
 * fecha mal leída declara en regla a un chofer que no lo está**, y queda
 * firmada con el nombre de quien apretó Ok.
 *
 * Este módulo no dibuja nada y no escribe nada: dice **qué se va a escribir,
 * qué se va a pisar y qué no se toca**, para que eso esté a la vista ANTES del
 * Ok. Un resumen que sólo muestra lo que salió se lee como si el registro
 * estuviera completo, y quien confirma firma también lo que falta.
 *
 * ── LA DIRECCIÓN NO ES LA ZONA, Y ÉSA ES LA DECISIÓN QUE SOSTIENE TODO ──────
 *
 * `zona` decide la **prórroga de 60 días** de provincia; la dirección es la
 * calle. Escribir una en la otra haría que `clasificar()` no reconozca nada y
 * ese chofer pierda la prórroga **sin fallar en ningún lado**.
 *
 * Van a columnas distintas, y la zona sólo se completa **si está vacía**. Si ya
 * tiene un valor y la localidad del registro dice otra cosa, se muestran las
 * dos y decide la persona: misma regla que el cotejo del alta de movimientos,
 * que `duplicados` y que el teléfono de la ficha — lo automático completa el
 * hueco, la persona corrige cualquier cosa.
 *
 * ── EL NOMBRE NO SE ESCRIBE NUNCA ───────────────────────────────────────────
 *
 * El del registro y el del padrón son dos formas de escribir a la misma
 * persona, y el padrón es el que usan el cotejo, los reportes y la ficha.
 * Pisarlo desde una foto haría que la misma persona se parta en dos en las
 * estadísticas. Si difieren, se dice.
 */

import { diaIsoAr } from "./fechaAr";

/* Los campos que la foto puede escribir. `nombre` NO está, a propósito. */
export const CAMPOS = ["registro_vence", "direccion", "zona"];

export const ETIQUETAS = {
  registro_vence: "Vence el registro",
  direccion: "Dirección",
  zona: "Zona",
};

const limpio = (v) => {
  const s = String(v ?? "").trim();
  return s || null;
};

const igual = (a, b) =>
  String(a ?? "").trim().toUpperCase() === String(b ?? "").trim().toUpperCase();

/* ¿Hay algo que valga la pena poner arriba de un botón que dice «Ok»?
 *
 * Hacen falta LAS DOS COSAS: saber **de quién** es el registro y tener **algo
 * que escribir**. Sin lo primero no hay fila a la que ir; sin lo segundo se
 * estaría pidiendo que alguien firme un papel en blanco.
 */
export function sePuedeConfirmar(lectura, dni) {
  if (!lectura?.ok) return false;
  if (!limpio(dni)) return false;
  return Boolean(limpio(lectura.vence) || limpio(lectura.direccion));
}

/* Qué pasaría si se apretara Ok.
 *
 *   cambios   lo que se va a escribir
 *   pisa      lo que ya tenía OTRO valor y se va a reemplazar, con el anterior
 *   noSeToca  lo que se leyó y no se escribe, con el motivo
 *
 * `pisa` y `noSeToca` son dos cosas distintas y por eso son dos listas: la
 * primera es una decisión que la persona toma al apretar, y la segunda es
 * información que si no se dice se pierde.
 */
export function comparar(lectura, fila = {}) {
  const cambios = {};
  const pisa = [];
  const noSeToca = [];

  const vence = limpio(lectura?.vence);
  if (vence) {
    /* La fecha SÍ pisa la que había, y es lo correcto: el papel que se acaba
     * de fotografiar es más nuevo que lo que dice la planilla, y renovar el
     * registro es justamente lo que hace que cambie. Lo que no puede pasar es
     * que pise en silencio — el valor anterior va a la vista. */
    const antes = diaIsoAr(limpio(fila.registro_vence)) || null;
    cambios.registro_vence = vence;
    if (antes && antes !== vence) {
      pisa.push({ campo: "registro_vence", antes, ahora: vence });
    }
  }

  const dir = limpio(lectura?.direccion);
  if (dir) {
    const antes = limpio(fila.direccion);
    cambios.direccion = dir;
    if (antes && !igual(antes, dir)) {
      pisa.push({ campo: "direccion", antes, ahora: dir });
    }
  }

  const loc = limpio(lectura?.localidad);
  if (loc) {
    const antes = limpio(fila.zona);
    if (!antes) {
      // El hueco se llena sin preguntar: para eso está.
      cambios.zona = loc;
    } else if (!igual(antes, loc)) {
      /* Y acá NO se pisa. La zona decide la prórroga de 60 días, y la que está
       * cargada la puso alguien que sabe de qué partido es esa persona; el
       * registro dice dónde vive, que puede ser otra cosa. Se muestran las dos
       * y decide quien mira. */
      noSeToca.push({
        campo: "zona",
        motivo: `ya dice «${antes}» y el registro dice «${loc}». La zona ` +
          `decide la prórroga de 60 días, así que no se pisa sola.`,
      });
    }
  }

  const nombre = limpio(lectura?.nombre);
  if (nombre && fila.nombre && !igual(nombre, fila.nombre)) {
    noSeToca.push({
      campo: "nombre",
      motivo: `el registro dice «${nombre}» y acá figura «${fila.nombre}». ` +
        `El nombre no se cambia desde una foto: es el que usan los reportes.`,
    });
  }

  return { cambios, pisa, noSeToca };
}

/* Las tres fechas del registro, para mostrarlas juntas.
 *
 * SÓLO SE GUARDA EL VENCIMIENTO Y SE MUESTRAN LAS TRES, que es lo que permite
 * notar la equivocada. Leer el nacimiento como vencimiento da 1975 y se ve
 * solo; leer el OTORGAMIENTO como vencimiento da una fecha creíble un par de
 * años en el pasado, y ésa nadie la discute — salvo que esté el otorgamiento
 * al lado.
 */
export function fechas(lectura) {
  return [
    ["vence", "Vence", lectura?.vence || null],
    ["otorgamiento", "Otorgado", lectura?.otorgamiento || null],
    ["nacimiento", "Nacimiento", lectura?.nacimiento || null],
  ];
}
